import { Buffer } from 'buffer';
import { Document, pdf } from '@react-pdf/renderer';
import IncomePlanPDF from './IncomePlanPDF';
import ClientProfilePDF from './ClientProfilePDF';
import AssetsByYearPDF from './AssetsByYearPDF';
import IncomeByYearPDF from './IncomeByYearPDF';
import CoverPagePDF from './CoverPagePDF';
import DefaultPlanPDF from './DefaultPlanPDF';
import DisclosurePagePDF from './DisclosurePagePDF';

window.Buffer = Buffer;

// Page numbers are computed here (not via @react-pdf's `fixed`+`render` page
// callback, which forces an expensive multi-pass layout that pegs the CPU on
// this report's large tables). IncomeByYear/AssetsByYear emit one page per
// 15-row chunk, so their page span is computed up front.
const ROWS_PER_PAGE = 15;
const chunkCount = (arr) => Math.max(1, Math.ceil(((arr || []).length || 0) / ROWS_PER_PAGE) || 1);

const ReportPDF = ({ clientData, reportOptions, chartImages = {} }) => {
  try {
    const date = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const incomeByYearPages = chunkCount(clientData.incomeByYearData);
    const assetsByYearPages = chunkCount(clientData.assetsByYearData);

    const totalPages = (reportOptions.cover_page ? 1 : 0)
      + (reportOptions.client_profile ? 1 : 0)
      + (reportOptions.default_retirement_plan ? 1 : 0)
      + (reportOptions.redesigned_income_plan ? 1 : 0)
      + (reportOptions.income_by_year ? incomeByYearPages : 0)
      + (reportOptions.assets_by_year ? assetsByYearPages : 0)
      + 1; // disclosure page (always included)

    let pageCounter = 1;

    return (
      <Document>
        {/* Cover Page */}
        {reportOptions.cover_page && (
          <CoverPagePDF clientData={clientData} date={date} pageNumber={pageCounter++} totalPages={totalPages} />
        )}

        {/* Client Profile Page */}
        {reportOptions.client_profile && (
          <ClientProfilePDF clientData={clientData} pageNumber={pageCounter++} totalPages={totalPages} />
        )}

        {/* Default Retirement Plan Page */}
        {reportOptions.default_retirement_plan && (
          <DefaultPlanPDF
            clientData={clientData}
            chartImages={chartImages}
            pageNumber={pageCounter++}
            totalPages={totalPages}
          />
        )}

        {/* Income Plan Page */}
        {reportOptions.redesigned_income_plan && (
          <IncomePlanPDF
            clientData={clientData}
            chartImages={chartImages}
            pageNumber={pageCounter++}
            totalPages={totalPages}
          />
        )}

        {/* Income By Year Page(s) */}
        {reportOptions.income_by_year && (() => {
          const start = pageCounter;
          pageCounter += incomeByYearPages;
          return (
            <IncomeByYearPDF
              chartImages={chartImages}
              clientData={clientData}
              pageNumber={start}
              totalPages={totalPages}
            />
          );
        })()}

        {/* Assets By Year Table Page(s) */}
        {reportOptions.assets_by_year && (() => {
          const start = pageCounter;
          pageCounter += assetsByYearPages;
          return (
            <AssetsByYearPDF
              chartImages={chartImages}
              clientData={clientData}
              pageNumber={start}
              totalPages={totalPages}
            />
          );
        })()}

        {/* Required full disclosure — always included (compliance requirement) */}
        <DisclosurePagePDF pageNumber={pageCounter++} totalPages={totalPages} />

      </Document>
    );
  } catch (error) {
    console.error('Error in ReportPDF component:', error);
    throw error;
  }
};

export const generatePDF = async (clientData, reportOptions, chartImages) => {
  try {
    // Validate client data before PDF generation
    if (!clientData) {
      throw new Error('Client data is required for PDF generation');
    }

    const MyDocument = (
      <ReportPDF
        clientData={clientData}
        reportOptions={reportOptions}
        chartImages={chartImages}
      />
    );

    const blob = await pdf(MyDocument).toBlob();

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${clientData?.lastname || 'Client'}_Financial_Report.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error('Error generating PDF:', error);
    console.error('Error stack:', error.stack);
    throw error; // Re-throw to see the full error in the calling function
  }
};

export default ReportPDF;